import { all, task, timeout } from 'ember-concurrency';
import { convertToUser } from '../utils/messaging/user';
import { findConversationByContact } from '../utils/messaging/conversation';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import {
  pageNumberInfinityModel,
  pageNumberInfinityOptions,
} from '../utils/messaging/infinity-model';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

const TOKEN_REFRESH_INTERVAL_MINS = 4.5;
const CONTACT_PAGE_SIZE = 15;

const optionDefaults = {
  contactsFilter: {},
  eventHandlers: {},
  messagingApiUrl: '',
};

export default class MessagingService extends Service {
  @service infinity;
  @service('websocket/messagingWebsocket') messagingWebsocket;
  @service store;

  @tracked conversations;
  @tracked currentProfile;
  @tracked contactPageActive;
  @tracked settingAwayStatus = false;
  @tracked contacts = [];
  options = optionDefaults;
  token;

  get sortedConversations() {
    return this.conversations?.content?.toSorted((a, b) => {
      const aUnread = this.currentProfile.unreadConversationIds.includes(a.id);
      const bUnread = this.currentProfile.unreadConversationIds.includes(b.id);
      if (aUnread && !bUnread) {
        return -1;
      } else if (!aUnread && bUnread) {
        return 1;
      } else {
        return new Date(b.updatedAt) - new Date(a.updatedAt);
      }
    });
  }

  get messagingApiUrl() {
    return this.options.messagingApiUrl;
  }

  get tokenRefreshIntervalMins() {
    return isEmberTesting() ? 0 : TOKEN_REFRESH_INTERVAL_MINS;
  }

  get unreadConversationCount() {
    return this.currentProfile?.unreadConversationIds?.length || 0;
  }

  get isAway() {
    return this.currentProfile?.isAway || false;
  }

  startTask = task({ drop: true }, async (options = {}) => {
    this.options = { ...optionDefaults, ...options };
    await this.loadApp.perform(this.options.contactsFilter);
  });

  stop() {
    this.pollMessagingToken.cancelAll();
    this.messagingWebsocket.closeSocket();
  }

  emitEvent(eventName, data) {
    const messagingClient = document.querySelector('#ember-messaging-client');
    const event = new CustomEvent('messagingEvent', { detail: { name: eventName, data } });
    messagingClient.dispatchEvent(event);
  }

  loadApp = task({ drop: true }, async contactsFilter => {
    await this.getMessagingToken.perform();
    await this.getCurrentProfile.perform();
    await this.getConversations.perform();

    if (this.currentProfile.isClient) {
      await this.getContacts.perform(contactsFilter);
      await this.populateVirtualConversations.perform();
    }

    this.pollMessagingToken.perform();
    this.messagingWebsocket.openSocket(this.messagingApiUrl, this.token);
  });

  getMessagingToken = task({ drop: true }, async () => {
    const response = await fetch('/messaging/tokens', {
      method: 'POST',
    });
    const json = await response.json();
    this.token = json.token;
  });

  getCurrentProfile = task({ drop: true }, async () => {
    this.currentProfile = await this.store.queryRecord('messaging-profile', {
      include: 'system_user',
    });

    let user = this.store
      .peekAll('messaging-user')
      .find(user => user.id === this.currentProfile.id);
    if (!user) {
      user = this.store.createRecord('messaging-user', {
        id: this.currentProfile.id,
        name: this.currentProfile.name,
        externalScope: this.currentProfile.externalScope,
      });
    }
    this.currentProfile.user = user;
  });

  getConversations = task({ drop: true }, async () => {
    const model = await this.infinity.model(
      'messaging-conversation',
      {
        include: 'users,last_message',
        ...pageNumberInfinityOptions(),
      },
      pageNumberInfinityModel()
    );

    this.#updateConversations(model);
  });

  getContacts = task({ drop: true }, async (filter = null) => {
    const model = await this.infinity.model(
      'messaging-contact',
      {
        ...(filter && { filter }),
        include: 'couple',
        ...pageNumberInfinityOptions(CONTACT_PAGE_SIZE),
      },
      pageNumberInfinityModel(CONTACT_PAGE_SIZE)
    );

    this.contacts = model;
  });

  populateVirtualConversations = task({ drop: true }, async () => {
    if (!this.contacts) {
      return;
    }
    const contactsWithoutConversations = this.contacts.filter(
      contact => !findConversationByContact(this.conversations, contact)
    );

    const virtualConversations = contactsWithoutConversations.map(contact =>
      this.buildVirtualConversation(contact)
    );
    this.conversations.unshiftObjects(virtualConversations);
  });

  buildVirtualConversation = contact => {
    let users = [this.currentProfile?.user];
    if (contact.couple.length) {
      users.push(...contact.couple.map(c => convertToUser(c, this.store)));
    } else {
      users.push(convertToUser(contact, this.store));
    }
    return this.store.createRecord('messaging-conversation', {
      users,
    });
  };

  refreshProfileAndConversations = task({ drop: true }, async () => {
    return all([this.getCurrentProfile.perform(), this.getConversations.perform()]);
  });

  toggleAwayStatus = task({ drop: true }, async isAway => {
    await this.store
      .adapterFor('messaging-profile')
      .updateAwayStatus(this.currentProfile.id, isAway);

    this.currentProfile.isAway = isAway;
  });

  updateAwayMessage = task({ drop: true }, async awayMessage => {
    await this.store
      .adapterFor('messaging-profile')
      .updateAwayMessage(this.currentProfile.id, awayMessage);

    this.currentProfile.awayMessageText = awayMessage;
  });

  pollMessagingToken = task(async () => {
    do {
      await timeout(this.tokenRefreshIntervalMins * 60 * 1000);
      await this.getMessagingToken.perform();
    } while (!isEmberTesting());
  });

  loadMoreConversations = task({ drop: true }, async model => {
    if (!model.canLoadMore) return;
    let data = await this.infinity.loadNextPage(model);

    this.#updateConversations(data);
  });

  loadMoreContacts = task({ drop: true }, async model => {
    if (!model.canLoadMore) return;
    await this.infinity.loadNextPage(model);
  });

  #updateConversations(data) {
    const filteredData = data.filter(conversation => {
      const { disabled, inactive } = conversation;

      return !disabled && !(this.currentProfile.isClient && inactive);
    });

    this.conversations = this.infinity.replace(data, filteredData);
  }

  navigateToContactPage = () => {
    this.contactPageActive = true;
  };

  toggleAwayStatusCustomizing() {
    this.settingAwayStatus = !this.settingAwayStatus;
  }
}
